import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { profileDefault } from 'config/dataDefaults';
import { useFormInputState } from 'hooks';
import {
  Button,
  FlexContainer,
  LabeledInput,
  Spinner,
  Txt,
  View,
  AgreePolicyBlock,
  InputPassword,
} from 'components';
import {
  firstNameField,
  lastNameField,
  passwordField,
  phoneField,
} from 'config/fieldsProps';
import { userTitleOptions } from 'config/userFields';
import { useAuthState } from 'state';
import { SelectAccountType } from './components';
import styles from './AuthRegister.module.scss';
import { errorCodeToMessage } from 'firebaseSDK';

/**
 * AuthRegister
 *
 * @return {JSX.Element}
 */
const AuthRegister = () => {
  const authState = useAuthState();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [state, handleChange, setKeyValue] = useFormInputState(profileDefault);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (state.password !== state.passwordConfirm) {
      setError("Passwords doesn't match");
      return;
    }

    setError('');
    setIsLoading(true);
    try {
      const result = await authState.register(state);
      console.log(result);
    } catch (e) {
      const { code } = e;
      const message = errorCodeToMessage[code] || 'Unknown error';
      setError(message);
      console.log(e.message);
    }
    setIsLoading(false);
    console.log(state);
  };

  const handleAccountType = (type) => setKeyValue('profileType', type);

  return (
    <FlexContainer
      column
      gap
      className={styles.AuthRegister}
      justifyContentCenter
    >
      <Helmet title="Sign-up" />
      {!state.profileType ? (
        <>
          <FlexContainer column gapHalf>
            <Txt xxxxx>What account would you like to create?</Txt>
            <Txt xx regular>
              Tap the account you want to create to continue.
            </Txt>
          </FlexContainer>
          <SelectAccountType onSelect={handleAccountType} />
        </>
      ) : (
        <FlexContainer column gap>
          <FlexContainer column gapHalf>
            <Txt xxxxx>Create your account</Txt>
            <Txt xx regular>
              Enter your login details, then tap continue.
            </Txt>
          </FlexContainer>
          <form onSubmit={handleSubmit} className={styles.Form}>
            <LabeledInput
              label="Title"
              name="title"
              onChange={handleChange}
              value={state.title}
              disabled={isLoading}
              options={userTitleOptions}
              required
              select
            />
            <LabeledInput
              {...firstNameField}
              label="First Name"
              onChange={handleChange}
              value={state.firstName}
              disabled={isLoading}
            />
            <LabeledInput
              {...lastNameField}
              label="Last Name"
              onChange={handleChange}
              value={state.lastName}
              disabled={isLoading}
            />
            <LabeledInput
              {...phoneField}
              label="Mobile number"
              onChange={handleChange}
              value={state.mobileNumber}
              disabled={isLoading}
            />
            <LabeledInput
              label="Email address"
              type="email"
              name="emailAddress"
              onChange={handleChange}
              value={state.emailAddress}
              disabled={isLoading}
              required
            />
            <LabeledInput label="Password">
              <InputPassword
                onChange={handleChange}
                value={state.password}
                disabled={isLoading}
                {...passwordField}
              />
            </LabeledInput>
            <LabeledInput label="Confirm Password">
              <InputPassword
                onChange={handleChange}
                value={state.passwordConfirm || ''}
                disabled={isLoading}
                {...passwordField}
                name="passwordConfirm"
              />
            </LabeledInput>
            {error ? (
              <View className={styles.FormFooter}>
                <Txt error>{error}</Txt>
              </View>
            ) : null}
            <Button success type="submit" disabled={isLoading}>
              {isLoading ? <Spinner /> : 'Continue'}
            </Button>
          </form>
          <AgreePolicyBlock preText="By creating an account" />
        </FlexContainer>
      )}
    </FlexContainer>
  );
};

AuthRegister.displayName = 'AuthRegister';

export default AuthRegister;
